import sortBy from "lodash/sortBy";
import type { StateCreator } from "zustand";

import type { Process, Test } from "@/repositories/process/types";
import type { Question } from "@/repositories/qae/types";
import { evaluateAnswer, isOpenQuestion } from "@/utils/demo";

interface TestDemoSliceState {
  questions: Question[];
  currentIndex: number;
  answers: Record<string, string[]>;
  showFeedback: boolean;
  isCorrect: boolean;
  isSubmitting: boolean;
  isCompleted: boolean;
  demoTest?: Test;
  demoProcess?: Process;
  demoToTake: Question[];
  demoShowToast: boolean;
  demoWasOpenQuestion: boolean;
  isCatTest?: boolean;
  isTimedOut: boolean;
  showTimeoutModal: boolean;
}

interface TestDemoSliceActions {
  initializeQuestions: (questions: Question[]) => void;
  submitAnswer: (questionId: string, answerId: string) => void;
  validateQuestion: () => boolean;
  nextQuestion: () => void;
  initializeDemo(test: Test, process: Process): void;
  setDemoToTake: (question: Question[]) => void;
  setDemoShowToast: (show: boolean) => void;
  setDemoWasOpenQuestion: (isOpen: boolean) => void;
  resetDemo: () => void;
  setIsCatTest: (isCat: boolean) => void;
  handleCatTimeout: () => void;
  acceptCatTimeout: () => void;
}

export type TestDemoSlice = TestDemoSliceState & TestDemoSliceActions;

export const createTestDemoSlice: StateCreator<
  TestDemoSlice,
  [["zustand/devtools", never]],
  [],
  TestDemoSlice
> = (set, get) => ({
  questions: [],
  currentIndex: 0,
  answers: {},
  showFeedback: false,
  isCorrect: false,
  isSubmitting: false,
  isCompleted: false,
  demoTest: undefined,
  demoProcess: undefined,
  demoToTake: [],
  demoShowToast: false,
  demoWasOpenQuestion: false,
  isCatTest: false,
  isTimedOut: false,
  showTimeoutModal: false,

  initializeQuestions: (questions) => {
    const sorted = sortBy(questions, "order");
    set({
      questions: sorted,
      currentIndex: 0,
      answers: {},
      isCompleted: false,
    });
  },

  validateQuestion: () => {
    const currentIndex = get().currentIndex;
    const currentQuestion = get().questions[currentIndex];
    const currentQuestionId = currentQuestion.id;
    const selectedAnswers = get().answers[currentQuestionId];

    return evaluateAnswer(currentQuestion, selectedAnswers);
  },

  submitAnswer: (questionId, answerId) => {
    const state = get();
    const question = state.questions[state.currentIndex];
    const currentAnswers = state.answers[questionId] || [];

    let newAnswers: string[];
    if (question.type === "MULTIPLE") {
      newAnswers = currentAnswers.includes(answerId)
        ? currentAnswers.filter((id) => id !== answerId)
        : [...currentAnswers, answerId];
    } else {
      newAnswers = [answerId];
    }

    set({
      answers: { ...state.answers, [questionId]: newAnswers },
    });
  },

  nextQuestion: () => {
    const state = get();
    const isCorrect = state.validateQuestion();
    const isLastQuestion = state.currentIndex === state.questions.length - 1;

    set({ demoShowToast: true });
    set({
      demoWasOpenQuestion: isOpenQuestion(state.questions[state.currentIndex]),
    });

    if (isCorrect) {
      set({
        showFeedback: true,
        isCorrect: true,
        isSubmitting: true,
      });

      setTimeout(() => {
        if (isLastQuestion) {
          set({
            isSubmitting: false,
            showFeedback: false,
            isCompleted: true,
          });
        } else {
          set((state) => ({
            isSubmitting: false,
            showFeedback: false,
            currentIndex: state.currentIndex + 1,
            answers: {},
          }));
        }
      }, 1500);
    } else {
      set({
        showFeedback: true,
        isCorrect: false,
        isSubmitting: false,
      });
    }
  },

  initializeDemo: (test, process) => {
    set({ demoTest: test, demoProcess: process });
  },

  setDemoToTake: (questions) => {
    set({ demoToTake: questions });
  },

  setDemoShowToast: (show) => {
    set({ demoShowToast: show });
  },

  setDemoWasOpenQuestion: (isOpen) => {
    set({ demoWasOpenQuestion: isOpen });
  },

  setIsCatTest: (isCat: boolean) => {
    set({ isCatTest: isCat });
  },

  handleCatTimeout: () => {
    set({
      isTimedOut: true,
      showTimeoutModal: true,
    });
  },

  acceptCatTimeout: () => {
    const state = get();
    const isCorrect = state.validateQuestion();
    const isLastQuestion = state.currentIndex === state.questions.length - 1;

    // Close modal and show feedback
    set({
      showTimeoutModal: false,
      showFeedback: true,
      isCorrect,
      isSubmitting: true,
    });

    // Wait 1 second to show feedback, then move to next question
    setTimeout(() => {
      if (isLastQuestion) {
        set({
          isSubmitting: false,
          showFeedback: false,
          isCompleted: true,
          isTimedOut: false,
        });
      } else {
        set((state) => ({
          isSubmitting: false,
          showFeedback: false,
          currentIndex: state.currentIndex + 1,
          answers: {},
          isTimedOut: false,
        }));
      }
    }, 1000);
  },

  resetDemo: () => {
    set({
      questions: [],
      currentIndex: 0,
      answers: {},
      showFeedback: false,
      isCorrect: false,
      isSubmitting: false,
      isCompleted: false,
      demoToTake: [],
      demoShowToast: false,
      demoWasOpenQuestion: false,
      isCatTest: false,
      isTimedOut: false,
      showTimeoutModal: false,
    });
  },
});
